import { useMemo, useState } from "react";

import {
  activateTariffById,
  getRelationLabel,

  getTariffs,
  normalizeTariff,
  saveTariffs,
  tariffStatuses,
} from "../utils/tariffImportUtils";
import { SuccessToast, useSuccessFeedback } from "./useSuccessFeedback";
import TrashIcon from "./common/TrashIcon";

const tabs = [
  { key: "overview", label: "Uebersicht" },
  { key: "prices", label: "Preisstaffel" },
  { key: "postal", label: "PLZ-Zonen" },
  { key: "packages", label: "Packmittel" },
  { key: "surcharges", label: "Zuschlaege" },
  { key: "toll", label: "Maut/Diesel" },
  { key: "log", label: "Importprotokoll" },
];

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("de-DE");
}

function statusLabel(status) {
  return tariffStatuses[status] || status || "Entwurf";
}

function countPrices(tariff) {
  return Object.values(tariff.prices || {}).reduce(
    (sum, row) => sum + Object.keys(row || {}).length,
    0
  );
}

function PreviewJson({ value }) {
  return <pre className="tariff-detail-json">{JSON.stringify(value || {}, null, 2)}</pre>;
}

function TariffDetailModal({ tariff, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");

  if (!tariff) {
    return null;
  }

  return (
    <div className="modal-backdrop">
      <div className="card tariff-detail-modal">
        <div className="section-head">
          <div>
            <h2>{tariff.tariffName}</h2>
            <p className="section-text">
              {tariff.carrierName} / {getRelationLabel(tariff)} / {tariff.tariffType}
            </p>
          </div>
          <button type="button" className="button button-secondary" onClick={onClose}>
            Schliessen
          </button>
        </div>

        <div className="tariff-detail-tabs">
          {tabs.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={`button ${activeTab === tab.key ? "button-dark" : "button-secondary"}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <div className="tariff-detail-grid">
            <span>Status</span><strong>{statusLabel(tariff.status)}</strong>
            <span>ID</span><strong>{tariff.id}</strong>
            <span>Spedition</span><strong>{tariff.carrierName}</strong>
            <span>Land/Relation</span><strong>{getRelationLabel(tariff)}</strong>
            <span>Tarifart</span><strong>{tariff.tariffType}</strong>
            <span>Gueltigkeit</span><strong>{tariff.validFrom || "-"} bis {tariff.validTo || "-"}</strong>
            <span>Importdatum</span><strong>{formatDate(tariff.importedAt)}</strong>
            <span>Importiert von</span><strong>{tariff.importedBy || "-"}</strong>
            <span>Datei</span><strong>{tariff.originalFileName || "-"}</strong>
            <span>Gewichtsstufen</span><strong>{tariff.weightBreaks?.length || 0}</strong>
            <span>Zonen</span><strong>{tariff.zones?.length || 0}</strong>
            <span>Preise</span><strong>{countPrices(tariff)}</strong>
            <span>Notiz</span><strong>{tariff.note || "-"}</strong>
          </div>
        )}

        {activeTab === "prices" && <PreviewJson value={tariff.prices} />}
        {activeTab === "postal" && <PreviewJson value={tariff.postalCodeZones} />}
        {activeTab === "packages" && <PreviewJson value={tariff.packageRules} />}
        {activeTab === "surcharges" && <PreviewJson value={tariff.surcharges} />}
        {activeTab === "toll" && <PreviewJson value={{ toll: tariff.toll, diesel: tariff.diesel }} />}
        {activeTab === "log" && <PreviewJson value={tariff.importLog} />}
      </div>
    </div>
  );
}

export default function TariffManagementPage() {
  const [tariffs, setTariffs] = useState(() => getTariffs().map(normalizeTariff));
  const [selectedTariff, setSelectedTariff] = useState(null);
  const { successVisible, triggerSuccessFeedback } = useSuccessFeedback();

  const sortedTariffs = useMemo(() => {
    return [...tariffs].sort((left, right) => String(right.importedAt || "").localeCompare(String(left.importedAt || "")));
  }, [tariffs]);

  const persist = (nextTariffs) => {
    setTariffs(nextTariffs);
    saveTariffs(nextTariffs);
    triggerSuccessFeedback();
  };

  const activateTariff = (tariffId) => {
    persist(activateTariffById(tariffs, tariffId));
  };

  const archiveTariff = (tariffId) => {
    persist(tariffs.map((tariff) => tariff.id === tariffId
      ? { ...tariff, status: "archived", isActive: false, updatedAt: new Date().toISOString() }
      : tariff
    ));
  };

  const deleteTariff = (tariffId) => {
    const tariff = tariffs.find((item) => item.id === tariffId);

    if (!window.confirm(`Tarif wirklich loeschen?\n\n${tariff?.tariffName || tariffId}`)) {
      return;
    }

    persist(tariffs.filter((item) => item.id !== tariffId));
    if (selectedTariff?.id === tariffId) {
      setSelectedTariff(null);
    }
  };

  return (
    <div className="page-stack tariff-management-page">
      <SuccessToast visible={successVisible} />
      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="page-title">Tarifverwaltung</h1>
        <p className="section-text">
          Import-Historie, Detailpruefung und Aktivierung je Spedition, Relation und Tarifart.
        </p>
      </div>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>Importierte Tarife</h2>
            <p className="section-text">Doppelklick auf eine Zeile oeffnet die Detailansicht.</p>
          </div>
        </div>

        {sortedTariffs.length === 0 ? (
          <div className="empty-state">Noch keine Tarife importiert.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table tariff-management-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Tarifname</th>
                  <th>Spedition</th>
                  <th>Land</th>
                  <th>Tarifart</th>
                  <th>Gueltigkeit</th>
                  <th>Importdatum</th>
                  <th>Datei</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sortedTariffs.map((tariff) => (
                  <tr key={tariff.id} onDoubleClick={() => setSelectedTariff(tariff)}>
                    <td><span className={`tariff-status-pill tariff-status-${tariff.status || "draft"}`}>{statusLabel(tariff.status)}</span></td>
                    <td>{tariff.tariffName}</td>
                    <td>{tariff.carrierName}</td>
                    <td>{getRelationLabel(tariff)}</td>
                    <td>{tariff.tariffType}</td>
                    <td>{tariff.validFrom || "-"} bis {tariff.validTo || "-"}</td>
                    <td>{formatDate(tariff.importedAt)}</td>
                    <td>{tariff.originalFileName || "-"}</td>
                    <td>
                      <div className="media-actions">
                        <button type="button" className="button button-inline-edit" onClick={() => setSelectedTariff(tariff)}>
                          Details
                        </button>
                        <button type="button" className="button button-primary" disabled={tariff.status === "active"} onClick={() => activateTariff(tariff.id)}>
                          Aktiv setzen
                        </button>
                        <button type="button" className="button button-secondary" disabled={tariff.status === "archived"} onClick={() => archiveTariff(tariff.id)}>
                          Archivieren
                        </button>
                        <button type="button" className="button button-inline-danger button-icon-danger" onClick={() => deleteTariff(tariff.id)} aria-label="Loeschen" title="Loeschen">
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <TariffDetailModal tariff={selectedTariff} onClose={() => setSelectedTariff(null)} />
    </div>
  );
}


